import { useState } from "react";
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_PRESETS,
  EQUIPMENT_SETUP_OPTIONS,
} from "../settingsOptions";
import {
  BODYWEIGHT_EQUIPMENT,
  applyEquipmentPreset,
  areEquipmentSetsEqual,
  isBodyweightOnly,
  normalizeEquipmentList,
  toggleAvailableEquipment,
} from "../equipmentPresetUtils";
import {
  Badge,
  Button,
  Dialog,
  SelectableMediaGrid,
  SelectableMediaTile,
} from "../../../design-v2";
import {
  CollapsibleBlock,
  SectionBlock,
  SelectableCard,
  applyDraftUpdate,
  findFieldError,
} from "./shared";

export default function EnvironmentSection({ draft, onChange, fieldErrors }) {
  const [pendingPreset, setPendingPreset] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);
  const rawEquipmentList =
    draft?.environment?.availableEquipment ?? draft?.environment?.equipmentList;
  const equipmentList = normalizeEquipmentList(rawEquipmentList);
  const equipmentPreset =
    draft?.environment?.equipmentPreset ?? draft?.environment?.equipmentSetup ?? null;
  const selectedPreset = EQUIPMENT_PRESETS[equipmentPreset] || null;
  const isCustomized = Boolean(
    equipmentPreset &&
      selectedPreset &&
      !areEquipmentSetsEqual(equipmentList, selectedPreset)
  );
  const lockedValues = isBodyweightOnly(equipmentList)
    ? [BODYWEIGHT_EQUIPMENT]
    : [];
  const pendingPresetOption = pendingPreset
    ? EQUIPMENT_SETUP_OPTIONS.find((option) => option.value === pendingPreset)
    : null;

  function updateEnvironment(producer) {
    applyDraftUpdate(draft, onChange, (nextDraft) => {
      if (!nextDraft.environment || typeof nextDraft.environment !== "object") {
        nextDraft.environment = {};
      }

      producer(nextDraft.environment);
      nextDraft.environment.availableEquipment = normalizeEquipmentList(
        nextDraft.environment.availableEquipment
      );
    });
  }

  function toggleEquipment(itemValue) {
    updateEnvironment((environment) => {
      environment.availableEquipment = toggleAvailableEquipment(
        equipmentList,
        itemValue
      );
    });
  }

  function applyPreset(presetValue) {
    updateEnvironment((environment) => {
      Object.assign(
        environment,
        applyEquipmentPreset(environment, presetValue)
      );
    });
    setOpenCategory(null);
  }

  function handlePresetClick(presetValue) {
    const isInitialState =
      !equipmentPreset &&
      (!Array.isArray(rawEquipmentList) ||
        rawEquipmentList.length === 0 ||
        isBodyweightOnly(equipmentList));

    if (isInitialState) {
      applyPreset(presetValue);
      return;
    }

    setPendingPreset(presetValue);
  }

  function cancelPresetReplace() {
    setPendingPreset(null);
  }

  function confirmPresetReplace() {
    if (pendingPreset) {
      applyPreset(pendingPreset);
    }
    setPendingPreset(null);
  }

  return (
    <div className="space-y-6">
      <SectionBlock
        title="Equipment Setup"
        description="Choose a preset, then fine-tune your actual available equipment."
        error={findFieldError(fieldErrors, ["environment.equipmentPreset"])}
      >
        <div className="space-y-3">
          {isCustomized ? <Badge tone="info">Customized</Badge> : null}
          {EQUIPMENT_SETUP_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              label={option.label}
              description={option.description}
              icon={option.icon}
              selected={equipmentPreset === option.value}
              onClick={() => handlePresetClick(option.value)}
            />
          ))}
        </div>
      </SectionBlock>

      <SectionBlock
        title="Equipment List"
        description="Open only the categories you need and tap the tools you actually have."
        error={findFieldError(fieldErrors, ["environment.availableEquipment"])}
      >
        <div className="space-y-3">
          {EQUIPMENT_CATEGORIES.map((category) => {
            const selectedItems = category.items.filter((item) =>
              equipmentList.includes(item.value)
            );

            return (
              <CollapsibleBlock
                key={category.label}
                title={category.label}
                badge={selectedItems.length > 0 ? `${selectedItems.length}` : null}
                isOpen={openCategory === category.label}
                onToggle={() =>
                  setOpenCategory((currentCategory) =>
                    currentCategory === category.label ? null : category.label
                  )
                }
              >
                {/*
                  * Presentation only: the same options, the same toggle and the
                  * same locked values the chip row used, rendered through the
                  * shared two-column tile grid.
                  */}
                <SelectableMediaGrid className="lz-settings-equipment-grid">
                  {category.items.map((item) => (
                    <SelectableMediaTile
                      key={item.value}
                      label={item.label}
                      selected={equipmentList.includes(item.value)}
                      disabled={lockedValues.includes(item.value)}
                      onClick={() => toggleEquipment(item.value)}
                    />
                  ))}
                </SelectableMediaGrid>
              </CollapsibleBlock>
            );
          })}
        </div>
      </SectionBlock>

      {/*
        Canonical Dialog: same trigger condition, same Cancel / Replace outcomes
        and the same resulting equipment state as the previous ad-hoc overlay.
      */}
      <Dialog
        size="sm"
        open={Boolean(pendingPresetOption)}
        onClose={cancelPresetReplace}
        title="Replace equipment selection?"
        closeLabel="Close replace equipment dialog"
        footer={
          <>
            <Button variant="secondary" className="flex-1" onClick={cancelPresetReplace}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={confirmPresetReplace}>
              Replace
            </Button>
          </>
        }
      >
        <p className="lz-settings-muted-text">
          This will replace your current equipment list with the{" "}
          {pendingPresetOption?.label} preset. You can still adjust individual equipment
          after.
        </p>
      </Dialog>
    </div>
  );
}
